import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, errorMessage, type Catalog, type LayoutWidget } from './dashboardApi';
import { LayoutEditor } from './LayoutEditor';
import { WidgetGrid } from './WidgetGrid';

interface Props {
  open: boolean;
  title: string;
  catalog: Catalog;
  initialWidgets: LayoutWidget[];
  lockedTitles?: Record<string, string>;
  /** Extra fields shown above the arranger (a template's name / description). */
  header?: ReactNode;
  saveLabel?: string;
  saving?: boolean;
  /** False keeps Save disabled (e.g. a template with no name yet). */
  canSave?: boolean;
  onSave: (widgets: LayoutWidget[]) => void;
  onClose: () => void;
  /** Buttons that sit on the left of the footer (e.g. "Restore assigned layout"). */
  footerLeft?: ReactNode;
}

/** Arrange widgets, preview the result with real data, then save - shared by "Customize my dashboard" and the template editor. */
export function LayoutEditorDialog({ open, title, catalog, initialWidgets, lockedTitles, header, saveLabel = 'Save', saving, canSave = true, onSave, onClose, footerLeft }: Props) {
  const [widgets, setWidgets] = useState<LayoutWidget[]>(initialWidgets);
  const [tab, setTab] = useState(0);
  useEffect(() => { if (open) { setWidgets(initialWidgets); setTab(0); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preview asks the server to load exactly what the draft would show THIS user - widgets they cannot view come back denied.
  const preview = useQuery({
    queryKey: ['dashboard-preview', JSON.stringify(widgets)],
    queryFn: () => dashboardApi.previewData(widgets),
    enabled: open && tab === 1 && widgets.length > 0,
    staleTime: 0,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {header}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Arrange" /><Tab label="Preview" />
        </Tabs>
        {tab === 0 && <LayoutEditor catalog={catalog} value={widgets} onChange={setWidgets} lockedTitles={lockedTitles} />}
        {tab === 1 && (
          widgets.length === 0 ? <Typography color="text.secondary">Add at least one widget to preview.</Typography>
            : preview.isLoading ? <LinearProgress />
            : preview.error ? <Alert severity="error">{errorMessage(preview.error)}</Alert>
            : (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>Preview with your own access and current data. Nothing is saved until you press {saveLabel}.</Alert>
                <WidgetGrid widgets={preview.data?.widgets ?? []} />
              </Box>
            )
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Box>{footerLeft}</Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={saving || !canSave} onClick={() => onSave(widgets)}>{saveLabel}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
